import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel } from "./common";
import { useFinanceLock } from "./FinanceLock";

const MODES = [["shared", "One shared finance password (set below)"], ["login", "Each person re-enters their own login password"], ["pin", "Each person uses their own finance PIN"]];

function Toggle({ id, label, checked, onChange }) {
  return <label className="flex items-center justify-between gap-3 py-1.5 text-sm text-zinc-700"><span>{label}</span><Switch data-testid={id} checked={checked} onCheckedChange={onChange} /></label>;
}

export function FinanceLockSettings() {
  const { lock, reload } = useFinanceLock();
  const [f, setF] = useState(null);
  useEffect(() => { if (lock) setF({ enabled: lock.enabled, mode: lock.mode, admin_exempt: lock.admin_exempt, hide_elsewhere: lock.hide_elsewhere, shared_password: "" }); }, [lock]);
  if (!f) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try { await api.put("/finance-lock", { ...f, shared_password: f.shared_password || null }); toast.success("Finance lock saved"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Panel title="Finance password lock" testid="finance-lock-settings">
      <p className="mb-3 text-xs text-zinc-500">Locks Budget, Costing & P&L, Expense Reports, Studio Finance Sheet and Studio Overheads. The password is asked every time one of these pages is opened.</p>
      <Toggle id="finance-lock-enabled" label="Lock finance pages" checked={f.enabled} onChange={set("enabled")} />
      <div className="my-2 space-y-1.5">
        {MODES.map(([v, l]) => (
          <label key={v} className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700" data-testid={`finance-lock-mode-${v}`}>
            <input type="radio" name="flmode" checked={f.mode === v} onChange={() => set("mode")(v)} className="accent-[#A9792C]" />{l}
          </label>
        ))}
      </div>
      {f.mode === "shared" && (
        <input data-testid="finance-lock-shared-password" type="password" value={f.shared_password} onChange={(e) => set("shared_password")(e.target.value)}
          placeholder={lock.shared_set ? "Shared password is set. Type a new one to change it" : "Set the shared finance password (min 6)"} className="mb-2 h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm outline-none focus:border-[#A9792C]" />
      )}
      <Toggle id="finance-lock-admin-exempt" label="Admin opens without a password" checked={f.admin_exempt} onChange={set("admin_exempt")} />
      <Toggle id="finance-lock-hide-elsewhere" label="Also hide finance amounts on other pages (Dashboard, Projects)" checked={f.hide_elsewhere} onChange={set("hide_elsewhere")} />
      <Button data-testid="finance-lock-save" className="mt-3" onClick={save}>Save finance lock</Button>
    </Panel>
  );
}
